import { keepPreviousData, useMutation, useQuery } from "@tanstack/react-query";
import {
  exportOrganizationAppointments,
  fetchOrganizationAppointments,
  fetchOrganizationStats,
  fetchOrganizations,
  resolveOrganizationLink,
  type OrganizationAppointmentsParams,
  type OrganizationExportParams,
  type OrganizationListParams,
  type OrganizationStatsParams,
} from "./organizationApi";
import { organizationKeys } from "./organization.keys";
import { useDebounce } from "../useDebounce";

export function useOrganizations(params: OrganizationListParams) {
  const debouncedKeyword = useDebounce(params.keyword ?? "", 400);

  return useQuery({
    queryKey: organizationKeys.list({
      ...params,
      keyword: debouncedKeyword || undefined,
    }),
    queryFn: ({ signal }) =>
      fetchOrganizations(
        { ...params, keyword: debouncedKeyword || undefined },
        signal
      ),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useOrganizationStats(
  id: number | null | undefined,
  params: OrganizationStatsParams
) {
  return useQuery({
    queryKey: organizationKeys.stats({ id: id ?? 0, ...params }),
    queryFn: ({ signal }) => fetchOrganizationStats(id ?? 0, params, signal),
    enabled: Boolean(id),
    staleTime: 15_000,
  });
}

export function useOrganizationAppointments(
  id: number | null | undefined,
  params: OrganizationAppointmentsParams
) {
  return useQuery({
    queryKey: organizationKeys.appointments({ id: id ?? 0, ...params }),
    queryFn: ({ signal }) =>
      fetchOrganizationAppointments(id ?? 0, params, signal),
    enabled: Boolean(id),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  });
}

export function useExportOrganizationAppointments() {
  return useMutation({
    mutationFn: ({
      id,
      params,
    }: {
      id: number;
      params: OrganizationExportParams;
    }) => exportOrganizationAppointments(id, params),
  });
}

export function useResolveOrganizationLink(token?: string | null) {
  return useQuery({
    queryKey: organizationKeys.resolveLink(token ?? ""),
    queryFn: ({ signal }) => resolveOrganizationLink(token ?? "", signal),
    enabled: Boolean(token),
    staleTime: 15_000,
  });
}